import { getClassSpellLimits } from '../../../shared/rules/spellPreparation';
import { getSpellbook } from '../../../shared/rules/wizard.mjs';
import { getLifeDomainSpells } from '../../../shared/rules/subclasses/cleric/life.mjs';
import { getDevotionOathSpells } from '../../../shared/rules/subclasses/paladin/devotion.mjs';
import { getMagicalDiscoveries } from '../../../shared/rules/subclasses/bard/lore.mjs';
import { getWordsOfCreationSpells, isBardSpellFor, magicalSecretsLabel } from '../../../shared/rules/bard.mjs';
import { getDruidicSpells } from '../../../shared/rules/druid.mjs';
import { getLandSpells } from '../../../shared/rules/subclasses/druid/land.mjs';

export const classSpellRecords = (form, classId) => (form?.spells || [])
  .filter((spell) => spell?.source?.sourceType === 'class' && spell.source.sourceId === classId);

/**
 * The cantrips and spells a class's new level has room for, and what can fill them: its class list up to the highest
 * spell level, without spells already taken or always prepared. A Wizard's level 1+ spells come from the spellbook,
 * counting the ones added at this level. From Bard 10 a Bard's spells also come from the Magical Secrets lists.
 */
export function levelUpSpellRoom(form, nextForm, classId, level, catalog, newBookSpells = []) {
  const limits = getClassSpellLimits(classId, level, nextForm);
  const owned = classSpellRecords(form, classId);
  const taken = new Set([...(form?.spells || []).map((spell) => spell?.spellId), ...getLifeDomainSpells(nextForm), ...getDevotionOathSpells(nextForm), ...getMagicalDiscoveries(nextForm), ...getDruidicSpells(nextForm), ...getLandSpells(nextForm), ...getWordsOfCreationSpells(nextForm)]);
  const savedBook = classId === 'wizard' ? getSpellbook(form) : null;
  const book = savedBook ? [...savedBook, ...newBookSpells] : null;
  const list = Object.entries(catalog || {}).map(([spellId, spell]) => ({ ...spell, spellId: spell.spellId || spellId }))
    .filter((spell) => (classId === 'bard' ? isBardSpellFor(nextForm, spell) : (spell.classes || []).map((name) => String(name).toLowerCase()).includes(classId)) && !taken.has(spell.spellId))
    .map((spell) => (classId === 'bard' && magicalSecretsLabel(spell) ? { ...spell, listLabel: magicalSecretsLabel(spell) } : spell));
  const byName = (a, b) => Number(a.level) - Number(b.level) || String(a.name).localeCompare(String(b.name));
  const cantrips = owned.filter((spell) => Number(spell.level) === 0).length;
  return {
    cantrips: { open: Math.max(0, limits.cantrips - cantrips), options: list.filter((spell) => Number(spell.level) === 0).sort(byName) },
    spells: { open: Math.max(0, limits.preparedSpells - (owned.length - cantrips)),
      options: list.filter((spell) => Number(spell.level) >= 1 && Number(spell.level) <= limits.maxSpellLevel && (!book || book.includes(spell.spellId))).sort(byName) },
  };
}

/** The selection a class saves once its new picks are added. Picks no longer offered are dropped. */
export function levelUpSpellSelection(form, classId, picks, room) {
  const offered = [...room.cantrips.options, ...room.spells.options];
  const added = [...picks.cantrips, ...picks.spells].map((id) => offered.find((spell) => spell.spellId === id)).filter(Boolean)
    .map(({ spellId, name, level }) => ({ spellId, name, level, source: { sourceType: 'class', sourceId: classId } }));
  return [...classSpellRecords(form, classId), ...added];
}
